import { Box, Flex, Text } from '@chakra-ui/layout';
import { Avatar, Icon } from '@chakra-ui/react';
import CardMy from 'components/CardMy';
import DatePicker from 'components/DatePicker';
import SelectMy from 'components/SelectMy';
import { getTime, subDays, subMonths } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { HiLightningBolt } from 'react-icons/hi';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchAgentStatistic, resetStatistic, selectAgentStatisticComputed, setSelectedUser } from 'store/slices/agent';
import { fetchCompanyUsers, selectCompanyUsers } from 'store/slices/companies';
import Statistics from './Statistics';

enum FILTER_VALUES {
    day = 'Day',
    week = 'Week',
    month = 'Month',
    date_range = 'Date range',
}

const UserInfo = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const users = useAppSelector(selectCompanyUsers);
    const { workTime } = useAppSelector(selectAgentStatisticComputed);
    const startOfDay = new Date().setHours(0, 0, 0, 0) as unknown as Date;
    const endOfDay = new Date().setHours(23, 59, 59, 999) as unknown as Date;
    const [user, setUser] = useState(null);
    const ref = useRef<HTMLDivElement>(null);

    const options = users?.map(item => ({
        label: item.name,
        value: item.id,
    }));

    const userName = user?.label;

    const [selectedDates, setSelectedDates] = useState<Date[]>([startOfDay, endOfDay]);

    const [filters, setFilters] = useState([
        { label: FILTER_VALUES.day, selected: true, value: [startOfDay, endOfDay] },
        { label: FILTER_VALUES.week, selected: false, value: [subDays(startOfDay, 7), startOfDay] },
        { label: FILTER_VALUES.month, selected: false, value: [subMonths(startOfDay, 1), startOfDay] },
        { label: FILTER_VALUES.date_range, selected: false, value: [null, null] },
    ]);

    const selectedFilter = filters.find(item => item.selected);

    const fetchData = () => {
        const params = {
            start: getTime(selectedDates[0]) as unknown as Date,
            end: getTime(new Date(selectedDates[1]).setHours(23, 59, 59, 999)) as unknown as Date,
            agentId: user?.value,
        };
        if (params.agentId && params.start && params.end) {
            dispatch(fetchAgentStatistic(params));
            // dispatch(fetchAgentStatisticByTimeframe({ ...params, activities: ['calls', 'dds', 'sms'] }));
        }
    };
    useEffect(() => {
        dispatch(fetchCompanyUsers());
        return () => {
            dispatch(resetStatistic());
        };
    }, []);

    useEffect(() => {
        fetchData();
        dispatch(setSelectedUser(user));
    }, [user?.value, selectedDates]);

    useEffect(() => {
        setSelectedDates(selectedFilter.value);
        if (selectedFilter.label === FILTER_VALUES.date_range) {
            ref.current.querySelector('input').click();
        }
    }, [selectedFilter]);

    return (
        <CardMy>
            <Flex mb="26px" gap="8px" flexWrap="wrap">
                <SelectMy
                    sx={{ width: '300px', marginRight: 'auto' }}
                    onChange={setUser}
                    value={user}
                    options={options}
                    placeholder="Select user"
                />
                <Flex flexWrap="wrap" gap="8px">
                    <div ref={ref}>
                        <DatePicker selectedDates={selectedDates} onDateChange={setSelectedDates} />
                    </div>
                    <Flex
                        borderRadius="100px"
                        borderColor="gray.200"
                        borderWidth="1px"
                        height="40px"
                        alignItems="center"
                        padding="4px"
                    >
                        {filters.map(filter => {
                            const handleClick = () => {
                                const changed = filters.map(item => ({
                                    ...item,
                                    selected: item.label === filter.label ? true : false,
                                }));
                                setFilters(changed);
                            };
                            const color = filter.selected ? 'brand.500' : 'gray.900';
                            const background = filter.selected ? 'brand.50' : 'transparent';
                            return (
                                <Box
                                    key={filter.label}
                                    fontSize="14px"
                                    fontWeight={500}
                                    borderRadius="100px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    as="button"
                                    onClick={handleClick}
                                    height="32px"
                                    background={background}
                                    color={color}
                                    paddingX="12px"
                                >
                                    {filter.label}
                                </Box>
                            );
                        })}
                    </Flex>
                </Flex>
            </Flex>
            <Flex mb="26px" alignItems="center" justifyContent="space-between">
                <Flex alignItems="center" gap="16px">
                    {userName && (
                        <>
                            <Avatar src={undefined} width="72px" height="72px" name={userName} />
                            <div>
                                <Text
                                    fontSize="24px"
                                    fontWeight="700"
                                    letterSpacing="-0.48px"
                                    color="gray.900"
                                    lineHeight={1}
                                    mb="8px"
                                >
                                    {userName}
                                </Text>
                                {/* <Text color="gray.900" fontWeight={500} display="flex" alignItems="center" gap="4px">
                                    <Icon color="inherit" as={HiLightningBolt} width="16px" height="16px" />
                                    ??%
                                    <Text color="gray.500" as="span" fontWeight={500}>
                                        Monthly Productivity
                                    </Text>
                                </Text> */}
                            </div>
                        </>
                    )}
                </Flex>
                <Box
                    background="linear-gradient(94deg, #5B6880 0%, #4A5568 100%)"
                    borderRadius="8px"
                    width="300px"
                    padding="16px"
                >
                    <Flex display="flex" alignItems="center" justifyContent="space-between" color="gray.300">
                        <Text fontSize="sm" fontWeight="500" display="flex" alignItems="center">
                            <Icon color="inherit" as={HiLightningBolt} width="16px" height="16px" marginRight="4px" />
                            RGAT
                        </Text>
                    </Flex>
                    <Text
                        fontSize="24px"
                        fontWeight="700"
                        letterSpacing="-0.48px"
                        color="white"
                        mt="8px"
                        lineHeight="1"
                    >
                        {workTime || '00:00:00'}
                    </Text>
                </Box>
            </Flex>
            <Statistics />
        </CardMy>
    );
};

export default UserInfo;
